import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import type { Group } from "../api.ts";
import styles from "../components/groups/Groups.module.css";
import { NewGroupButton } from "../components/groups/NewGroupButton.tsx";
import { Icon } from "../components/Icon.tsx";
import { Avatar } from "../components/people/PersonRow.tsx";
import { StatePanel } from "../components/StatePanel.tsx";
import ui from "../components/ui.module.css";
import { formatCount, formatNumber } from "../lib/format.ts";
import { groupsQuery, messageOf } from "../lib/queries.ts";

export const Route = createFileRoute("/groups/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(groupsQuery),
  component: Groups,
  pendingComponent: GroupsPending,
  errorComponent: GroupsError,
});

const FACES = 4;

function Groups() {
  const { data } = useSuspenseQuery(groupsQuery);
  const groups = data.items;

  useEffect(() => {
    document.title = "Groups · Vault";
  }, []);

  const people = new Set(groups.flatMap((group) => group.members)).size;

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <div className={styles.heading}>
          <h1 className={styles.title}>Groups</h1>
          {groups.length > 0 ? (
            <dl className={styles.stats}>
              <div>
                <dt>Groups</dt>
                <dd>{formatNumber(groups.length)}</dd>
              </div>
              <div>
                <dt>People</dt>
                <dd>{formatNumber(people)}</dd>
              </div>
            </dl>
          ) : null}
        </div>
        {groups.length > 0 ? <NewGroupButton className={styles.pageAction} /> : null}
      </div>

      {groups.length === 0 ? (
        <div className={`${ui.card} ${styles.empty}`}>
          <div className={styles.emptyArt} aria-hidden="true">
            <Avatar seed="amy" />
            <span className={styles.emptyBlob}>
              <Icon name="people" />
            </span>
            <Avatar seed="bo" />
          </div>
          <h2 className={styles.emptyTitle}>No groups yet</h2>
          <p className={styles.emptyDetail}>
            A group shares a file with everyone in it. People added later see every file it is on.
          </p>
          <NewGroupButton />
        </div>
      ) : (
        <ul className={`${ui.card} ${styles.list}`}>
          {groups.map((group) => (
            <GroupRow key={group.id} group={group} />
          ))}
        </ul>
      )}
    </div>
  );
}

function GroupRow({ group }: { group: Group }) {
  const faces = group.members.slice(0, FACES);
  const more = group.members.length - faces.length;
  return (
    <li className={styles.row}>
      <Avatar seed={group.name} group />
      <div className={styles.rowText}>
        <Link to="/groups/$id" params={{ id: group.id }} className={styles.rowLink}>
          {group.name}
        </Link>
        <p className={styles.rowMeta}>
          <span>{formatCount(group.members.length, "person", "people")}</span>
          <span data-shared={group.usedBy.length > 0 || undefined}>
            {group.usedBy.length > 0
              ? `On ${formatCount(group.usedBy.length, "file", "files")}`
              : "Not on any file"}
          </span>
        </p>
      </div>
      {faces.length > 0 ? (
        <span className={styles.faces} aria-hidden="true">
          {faces.map((email) => (
            <Avatar key={email} seed={email} />
          ))}
          {more > 0 ? <span className={styles.more}>+{formatNumber(more)}</span> : null}
        </span>
      ) : null}
      <Icon name="chevron" className={styles.chevron} />
    </li>
  );
}

function GroupsPending() {
  return (
    <div className={styles.page} aria-busy="true">
      <div className={styles.intro}>
        <div className={styles.heading}>
          <h1 className={styles.title}>Groups</h1>
          <span className={`${ui.skeleton} ${styles.statsSkeleton}`} />
        </div>
      </div>
      <div className={`${ui.card} ${styles.list}`}>
        {["first", "second", "third"].map((key) => (
          <div key={key} className={styles.pendingRow}>
            <span className={ui.skeleton} />
            <span className={ui.skeleton} />
          </div>
        ))}
      </div>
    </div>
  );
}

function GroupsError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  return (
    <StatePanel icon="alert" title="Groups did not load" detail={messageOf(error)}>
      <button
        type="button"
        className={ui.button}
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        <Icon name="retry" />
        Try again
      </button>
    </StatePanel>
  );
}
